import React from 'react';
import { makeStyles } from '@material-ui/styles';
import { colors } from '@skyslope/mache';
import { IS_MOBILE } from '../common/constants';
import type { IBlock } from '../store/types';

const useStyles = makeStyles(() => ({
  nextButton: {
    zIndex: 4,
    padding: `${IS_MOBILE ? '4px 8px' : '4px 24px'}`,
    right: '100%',
    width: `${IS_MOBILE ? '16px' : '36px'}`,
    backgroundColor: colors.blue[600],
    color: '#ffffff',
    fontSize: `${IS_MOBILE ? '12px' : '16px'}`,
    fontWeight: 'bold',
    height: `${IS_MOBILE ? '12px' : '24px'}`,
    userSelect: 'none',
    cursor: 'pointer',
    transform: 'translateY(-50%)',
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:after': {
      content: '""',
      width: 0,
      height: 0,
      borderStyle: 'solid',
      borderWidth: `${IS_MOBILE ? '10px 0 10px 10px' : '16px 0 16px 16px'}`,
      borderColor: `transparent transparent transparent ${colors.blue[600]}`,
      position: 'absolute',
      left: '100%',
      top: 0,
    },
  },
}));

interface IProps {
  zoom: number;
  selectedBlock?: IBlock;
  currentSelectedBlockIndex: number | null;
  documentIndex: number;
  documentId: string;
  onClick: () => void;
  pageDimensions: { height?: number; width?: number };
}

export function NextButton(props: IProps) {
  const classes = useStyles();
  const arrowRef = React.useRef<HTMLDivElement>(null);
  const [isArrowInside, setIsArrowInside] = React.useState(false);

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const checkIfArrowIsVisible = () => {
    if (!arrowRef.current) {
      return;
    }
    const rect = arrowRef.current!.getBoundingClientRect();
    if (isArrowInside && rect.left > 84) {
      setIsArrowInside(false);
    } else if (!isArrowInside && rect.left <= 0) {
      setIsArrowInside(true);
    }
  };

  React.useEffect(() => {
    // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
    const fnWrapper = () => checkIfArrowIsVisible();
    window.addEventListener('resize', fnWrapper);
    setTimeout(() => {
      checkIfArrowIsVisible();
    }, 0);
    return () => window.removeEventListener('resize', fnWrapper);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArrowInside, props.zoom]);

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const getTopValue = () =>
    props.selectedBlock
      ? (props.selectedBlock.y / props.pageDimensions.height!) * 100 +
        ((props.selectedBlock.height! / props.pageDimensions.height!) * 100) / 2
      : '';

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleClick = (e: any) => {
    e.stopPropagation();
    props.onClick();
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const showNextButton = () => {
    const { currentSelectedBlockIndex, documentIndex, documentId, selectedBlock } = props;
    if (
      (currentSelectedBlockIndex === null && documentIndex === 0) ||
      (selectedBlock && selectedBlock.documentId === documentId)
    ) {
      return true;
    }
    return false;
  };

  const nextButton = showNextButton() ? (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={arrowRef}
      className={classes.nextButton}
      style={{
        top: `${getTopValue()}%`,
        right: isArrowInside ? undefined : '100%',
        left: isArrowInside && !IS_MOBILE ? 0 : undefined,
      }}
      onClick={handleClick}
      onTouchEnd={handleClick}
    >
      {props.selectedBlock !== undefined ? 'Next' : 'Start'}
    </div>
  ) : null;

  return nextButton;
}
